import {
	type MouseEventHandler,
	type ReactNode,
	useEffect,
	useState,
} from 'react';

import Accordion from 'components/Accordion';
import {
	LoadingProductCardSkeletonItem,
	LoadingSkeleton,
} from 'components/LoadingSkeleton';
import LoadMoreList from 'components/LoadMoreList';
import Message from 'components/Message';
import ProductCard from 'components/ProductCard';
import Text from 'components/Text';
import {
	type GTMItemListId,
	type GTMItemListName,
	useProductListGTMEvents,
} from 'hooks/gtm';
import type { RelatedProductCard } from 'models/productCard';
import { is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

interface BaseProps {
	accordionId: string;
	afterListContent?: ReactNode;
	contentClassName?: string;
	engagementType?: 'addAccessoryToCart' | 'addArticleToCart';
	expiredProductsTitle: string;
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
	hasNextPage: boolean;
	isLoadingInitial: boolean;
	isLoadingMore: boolean;
	loadMoreButtonText: string;
	noProductsText: string;
	onLoadMoreClick: () => void;
	products: RelatedProductCard[] | undefined;
	title: string;
}

interface PropsWithState extends BaseProps {
	initialOpen?: never;
	isOpen: boolean;
	/** Trigger click handler to toggle the `isOpen` state. */
	onClick: MouseEventHandler<HTMLButtonElement>;
}

interface PropsWithoutState extends BaseProps {
	/** Initial open state when uncontrolled. */
	initialOpen?: boolean;
	isOpen?: never;
	onClick?: never;
}

type Props = PropsWithState | PropsWithoutState;

export default function RelatedProductsAccordion({
	accordionId,
	afterListContent,
	contentClassName,
	engagementType,
	expiredProductsTitle,
	gtmItemListId,
	gtmItemListName,
	hasNextPage,
	initialOpen,
	isLoadingInitial,
	isLoadingMore,
	isOpen,
	loadMoreButtonText,
	noProductsText,
	onClick,
	onLoadMoreClick,
	products,
	title,
}: Props) {
	const { tPlural } = useI18n();
	const hasProducts = is.arrayWithLength(products);
	const regularProducts = products?.filter((product) => !product.expired) ?? [];
	const expiredProducts = products?.filter((product) => product.expired) ?? [];

	// Use the first page count as page size.
	const [pageSize, setPageSize] = useState(products?.length ?? 0);
	useEffect(() => {
		if (!pageSize && products?.length) {
			setPageSize(products.length);
		}
	}, [products, pageSize]);

	const { sendViewItemListEvent, sendViewItemListEventOnce } =
		useProductListGTMEvents(gtmItemListId, gtmItemListName);

	// The readiness of the initial list depends on combinations of several
	// factors: initial or user triggered opening and if products are loading
	// or already available. Use a limited callback so the event isn't sent
	// for every close + open.
	useEffect(() => {
		if ((isOpen || initialOpen) && products?.length && pageSize) {
			sendViewItemListEventOnce(products, pageSize);
		}
	}, [isOpen, initialOpen, products, pageSize, sendViewItemListEventOnce]);

	return (
		// @ts-expect-error The initial open or isOpen and onClick case is handled
		// in the prop interface but TS doesn't see it.
		<Accordion
			id={accordionId}
			title={title}
			titleTag="h2"
			initialOpen={initialOpen}
			isOpen={isOpen}
			onClick={onClick}
		>
			<div className={`mt-2 mb-5 ${contentClassName}`}>
				{isLoadingInitial && (
					<LoadingSkeleton className="space-y-8">
						{range(3).map((key) => (
							<LoadingProductCardSkeletonItem
								key={key}
								orientation="horizontal"
								showAddToCartButton
							/>
						))}
					</LoadingSkeleton>
				)}
				{!isLoadingInitial && (
					<>
						{hasProducts && (
							<>
								<LoadMoreList
									loadedItemFocusTarget="first-interactive"
									hasLoadMoreButton={hasNextPage}
									isLoading={isLoadingMore}
									onLoadMoreClick={onLoadMoreClick}
									onLoadMoreDone={() => {
										sendViewItemListEvent(products, pageSize);
									}}
									buttonText={loadMoreButtonText}
									itemCountScreenReaderText={tPlural(
										'general_amount_of_products',
										products?.length ?? 0,
									)}
									listClassName="space-y-8"
									buttonClassName="mt-8"
								>
									{regularProducts.map((product, i) => (
										<ProductCard
											key={product.id}
											orientation="horizontal"
											product={product}
											productListIndex={i}
											gtmItemListId={gtmItemListId}
											gtmItemListName={gtmItemListName}
											showAddToCartButton
											showAddToWishlistButton
											openAddedProductPopover
											addToCartEngagementType={engagementType}
										/>
									))}
									{is.arrayWithLength(expiredProducts) && (
										<>
											<Text as="h3" text={expiredProductsTitle} />
											{expiredProducts.map((product, i) => (
												<ProductCard
													key={product.id}
													orientation="horizontal"
													product={product}
													productListIndex={i + regularProducts.length}
													gtmItemListId={gtmItemListId}
													gtmItemListName={gtmItemListName}
												/>
											))}
										</>
									)}
								</LoadMoreList>
								{afterListContent}
							</>
						)}
						{!hasProducts && (
							<Message
								variant="information"
								text={noProductsText}
								className="mt-4"
							/>
						)}
					</>
				)}
			</div>
		</Accordion>
	);
}
RelatedProductsAccordion.displayName = 'RelatedProductsAccordion';
